import { TransactionStatus, PaymentMethod } from 'store/billing'
import { useAppSelector } from 'store/hooks'
import { useCallback, useEffect } from 'react'
import {
  TrackingEventAction,
  TrackingEventCategory,
  TrackingEventName,
  useTrackingHelper,
} from 'utils/useTrackingHelper'

export default function useMatomoForPaymentTracking(): void {
  const { trackEvent } = useTrackingHelper()

  const { transactionStatus, paymentMethod } = useAppSelector(s => s.payments)

  const sendMatomoPaymentMethodEvent = useCallback(
    (paymentMethod: PaymentMethod): void => {
      let paymentMethodTrackingName: TrackingEventName = TrackingEventName.CreditCard

      switch (paymentMethod) {
        case PaymentMethod.CREDIT:
          paymentMethodTrackingName = TrackingEventName.CreditCard
          break
        case PaymentMethod.CRYPTO:
          paymentMethodTrackingName = TrackingEventName.Crypto
          break
        case PaymentMethod.APPLE:
        case PaymentMethod.GOOGLE:
        case PaymentMethod.AMAZON:
        case PaymentMethod.BROWSER:
          // 'PAYMENT_REQUEST' so TrackingEventName has a catch-all for these: XPay
          paymentMethodTrackingName = TrackingEventName.XPay
          break
      }
      trackEvent({
        category: TrackingEventCategory.Payments,
        action: TrackingEventAction.Method,
        name: paymentMethodTrackingName,
      })
    },
    [trackEvent],
  )

  const sendMatomoPaymentSuccessOrFailureEvent = useCallback(
    (isSuccess: boolean, paymentMethod: PaymentMethod): void => {
      let paymentMethodTrackingName: TrackingEventName = TrackingEventName.CreditCard
      switch (paymentMethod) {
        case PaymentMethod.CREDIT:
          paymentMethodTrackingName = TrackingEventName.CreditCard
          break
        case PaymentMethod.CRYPTO:
          paymentMethodTrackingName = TrackingEventName.Crypto
          break
        case PaymentMethod.APPLE:
        case PaymentMethod.GOOGLE:
        case PaymentMethod.AMAZON:
        case PaymentMethod.BROWSER:
          // 'PAYMENT_REQUEST' so TrackingEventName has a catch-all for these: XPay
          paymentMethodTrackingName = TrackingEventName.XPay
          break
      }

      const paymentMethodTrackingAction: TrackingEventAction = isSuccess
        ? TrackingEventAction.Success
        : TrackingEventAction.Failure

      trackEvent({
        category: TrackingEventCategory.Payments,
        action: paymentMethodTrackingAction,
        name: paymentMethodTrackingName,
      })
    },
    [trackEvent],
  )

  useEffect(() => {
    switch (transactionStatus) {
      case TransactionStatus.INPROGRESS:
        sendMatomoPaymentMethodEvent(paymentMethod)
        break
      // TODO: Fine tune. Maybe handle stopped and failure differently?
      case TransactionStatus.FAILURE:
      case TransactionStatus.STOPPED:
        sendMatomoPaymentSuccessOrFailureEvent(false, paymentMethod)
        break
      case TransactionStatus.SUCCESS:
        sendMatomoPaymentSuccessOrFailureEvent(true, paymentMethod)
        break
      default:
    }
  }, [
    paymentMethod,
    sendMatomoPaymentMethodEvent,
    sendMatomoPaymentSuccessOrFailureEvent,
    transactionStatus,
  ])
}
